import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { LocalizedLink as Link } from "../components/LocalizedLink";
import { useMessages } from "../context/MessagesContext";
import { getTratamientosData } from "../lib/tratamientosData";

export default function TratamientoDetalle() {
  const { slug = "" } = useParams();
  const labels = useMessages();
  const treatments = getTratamientosData(labels);
  const treatment = treatments.find((item) => item.slug === slug.toLowerCase());

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  if (!treatment) {
    return (
      <section id="tratamientos" className="scroll-mt-24 py-12">
        <div className="section-surface max-w-2xl mx-auto p-6 text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-3">Tratamiento no encontrado</h1>
          <p className="text-gray-600 mb-5">
            No encontramos un tratamiento para la URL{" "}
            <span className="font-semibold">/tratamientos/{slug}</span>.
          </p>
          <Link
            to="/tratamientos"
            className="inline-flex items-center justify-center rounded-lg bg-turquoise text-white px-4 py-2 font-semibold hover:bg-skyblue transition-colors"
          >
            Ver todos los tratamientos
          </Link>
        </div>
      </section>
    );
  }

  const related = treatments.filter((item) => item.slug !== treatment.slug).slice(0, 3);
  const localIntro = (
    labels["treatments.detail.localIntro"] ||
    "Tratamiento en Tijuana con valoración previa, orientación personalizada y aplicación profesional en Hidromed MX."
  ).replace("{title}", treatment.title);

  return (
    <section id="tratamientos" className="scroll-mt-24 py-8">
      <div className="max-w-8xl mx-auto px-4 space-y-6">
        <nav className="text-sm text-gray-500">
          <Link to="/" className="hover:underline">
            {labels["nav.home"] || "Inicio"}
          </Link>
          <span className="mx-2">/</span>
          <Link to="/tratamientos" className="hover:underline">
            {labels["nav.tratamientos"] || "Tratamientos"}
          </Link>
          <span className="mx-2">/</span>
          <span className="text-gray-700 font-semibold">{treatment.title}</span>
        </nav>

        <article className="section-surface overflow-hidden">
          <div className="h-72 md:h-96 w-full overflow-hidden">
            <img
              src={treatment.image}
              alt={treatment.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="p-6 md:p-8 space-y-5">
            <div className="flex flex-wrap gap-2">
              {treatment.price && (
                <span className="inline-block bg-green-100 text-turquoise font-semibold text-xs px-3 py-1 rounded-full">
                  {treatment.price}
                </span>
              )}
              {treatment.age && (
                <span className="inline-block bg-turquoise/20 text-turquoise font-semibold text-xs px-3 py-1 rounded-full">
                  {treatment.age}
                </span>
              )}
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 border-b-2 border-turquoise pb-2">
              {treatment.title}
            </h1>

            <p className="lead-copy">{localIntro}</p>

            <p className="body-copy leading-relaxed">{treatment.description}</p>

            <div className="grid gap-2">
              {treatment.aditional.map((benefit, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-turquoise rounded-full flex-shrink-0 mt-2" />
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                to="/reservar"
                className="btn btn--primary btn--shadow"
              >
                {labels["reserve.treatment"] || "Reservar este tratamiento"}
              </Link>
              <Link
                to="/tratamientos"
                className="inline-flex items-center justify-center rounded-lg border border-turquoise text-turquoise px-4 py-2 font-semibold hover:bg-turquoise/10 transition-colors"
              >
                {labels["reserve.treatments"] || "Ver todos los tratamientos"}
              </Link>
            </div>
          </div>
        </article>

        <div className="section-surface p-5">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            {labels["reserve.related"] || "Tratamientos relacionados"}
          </h2>
          <div className="flex flex-wrap gap-2">
            {related.map((item) => (
              <Link
                key={item.slug}
                to={`/tratamientos/${item.slug}`}
                className="inline-flex items-center justify-center rounded-full bg-turquoise/15 text-turquoise px-3 py-1 text-sm font-medium hover:bg-turquoise/25 transition-colors"
              >
                {item.title}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
